<template>
  <div class="chatbot-container" v-if="!navOpen">
    <!-- Botón para abrir el chatbot -->
    <div class="chatbot-button" @click="toggleChatbot" :class="{ 'active': isOpen }">
      <font-awesome-icon :icon="chatIcon" class="chatbot-icon" />
    </div>

    <!-- Contenedor del chat -->
    <transition name="slide">
      <div v-if="isOpen" class="chatbot-window">
        <div class="chatbot-header">
          <div class="chatbot-title">NOE - 🤖 Asistente Virtual</div>
          <div class="close-button" @click="toggleChatbot">
            <font-awesome-icon :icon="closeIcon" />
          </div>
        </div>
        <div class="chatbot-content">
          <div class="chatbot-messages" ref="messagesContainer">
            <div v-for="(message, index) in messages" :key="index"
                 :class="['message', message.type === 'user' ? 'user-message' : 'bot-message']">
              <div class="message-content" v-html="formatMessage(message.text)"></div>
            </div>
            <!-- Mostrar botones Sí/No solo hay feedback_request -->
            <div v-if="feedback_request" class="feedback-buttons-container">
              <div class="message-buttons">
                <button class="btn-response btn-yes" @click="handleButtonClick('Si')">Sí 👍</button>
                <button class="btn-response btn-no" @click="handleButtonClick('No')">No 👎</button>
              </div>
            </div>
            <div v-if="isLoading" class="message bot-message">
              <div class="message-content typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
          <div class="chatbot-input">
            <input
              type="text"
              v-model="userInput"
              @keyup.enter="sendMessage"
              placeholder="Escribe tu consulta aquí..."
              :disabled="isLoading"
            />
            <button @click="sendMessage" class="send-button" :disabled="isLoading">
              <font-awesome-icon :icon="sendIcon" />
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import { faCommentDots, faTimes, faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { mapGetters, mapActions } from 'vuex';
import { eventBus } from '@/main';

export default {
  name: 'Chatbot',
  components: {
    FontAwesomeIcon
  },
  data () {
    return {
      navOpen: false,
      userInput: '',
      isLoading: false,
      welcomeMessage: {
        text: `¡Bienvenido, soy NOE tu asistente virtual de Glucovibes! 👋

Para obtener la mejor experiencia y respuestas más precisas, sigue estas pautas:

✅ CÓMO HACER CONSULTAS EFECTIVAS:
• Sé específico en tus consultas
• Haz las consultas en español
• Escribe tu consulta en un solo mensaje
• Escribe una sola consulta por mensaje

❌ EVITA:
• Consultas muy generales o ambiguas
• Enviar múltiples mensajes para una sola consulta
• Consultas que no estén relacionadas con nuestros productos o servicios
• Compartir información personal sensible o confidencial
• Usar este chat para emergencias médicas - contacta a tu médico o servicios de emergencia

🔍 TEMAS QUE PUEDES CONSULTAR:
• Información sobre productos y servicios de GlucoVibes
• Consultas frecuentes sobre salud y alimentación
• Consejos y consultas nutricionales
• Dudas técnicas y resolución de problemas
• Información de contacto de GlucoVibes

Si tienes problemas técnicos persistentes, nuestro equipo de soporte está disponible para ayudarte directamente.

¿En que puedo ayudarte hoy?😊
`,
        type: 'bot'
      }
    };
  },
  computed: {
    ...mapGetters({
      messages: 'getMessages',
      isOpen: 'isOpen',
      feedback_request: 'getFeedbackRequest'
    }),
    chatIcon () {
      return faCommentDots;
    },
    closeIcon () {
      return faTimes;
    },
    sendIcon () {
      return faPaperPlane;
    }
  },
  created () {
    // Escuchar eventos del menú lateral
    eventBus.$on('navOpened', this.handleNavOpened);
    eventBus.$on('navClosed', this.handleNavClosed);
    eventBus.$on('secondaryNavOpened', this.handleNavOpened);
    eventBus.$on('secondaryNavClosed', this.handleNavClosed);
    eventBus.$on('closeNavs', this.handleNavClosed);

    // Inicializar el chatbot con el mensaje de bienvenida si no hay mensajes
    if (this.messages.length === 0) {
      this.addMessage(this.welcomeMessage);
    }
  },
  beforeDestroy () {
    // Eliminar listeners cuando el componente se destruye para evitar memory leaks
    eventBus.$off('navOpened', this.handleNavOpened);
    eventBus.$off('navClosed', this.handleNavClosed);
    eventBus.$off('secondaryNavOpened', this.handleNavOpened);
    eventBus.$off('secondaryNavClosed', this.handleNavClosed);
    eventBus.$off('closeNavs', this.handleNavClosed);
  },
  methods: {
    ...mapActions([
      'toggleChatbot',
      'addMessage',
      'sendQuery'
    ]),
    handleNavOpened () {
      this.navOpen = true;
      // Si el chatbot está abierto, cerrarlo
      if (this.isOpen) {
        this.toggleChatbot();
      }
    },
    handleNavClosed () {
      this.navOpen = false;
    },
    // Manejador para los clics en los botones
    handleButtonClick (response) {
      // Indicar que estamos esperando respuesta
      this.isLoading = true;

      // Enviar la selección como una consulta al chatbot
      this.sendQuery({
        context: this,
        query: response
      })
        .then(() => {
          this.scrollToBottom();
          this.isLoading = false;
        })
        .catch(() => {
          this.addMessage({
            text: 'Lo siento, ha ocurrido un error al procesar tu respuesta. Por favor, inténtalo de nuevo más tarde.',
            type: 'bot'
          });
          this.isLoading = false;
        });
    },
    sendMessage () {
      if (this.userInput.trim() === '' || this.isLoading) return;

      // Guardar el mensaje del usuario y limpiar el input
      const userText = this.userInput;
      this.userInput = '';

      // Indicar que estamos esperando respuesta
      this.isLoading = true;

      // Enviar consulta al store
      this.sendQuery({
        context: this,
        query: userText
      })
        .then(() => {
          this.scrollToBottom();
          this.isLoading = false; // Restablecer isLoading a false después de éxito
        })
        .catch(() => {
          this.addMessage({
            text: 'Lo siento, ha ocurrido un error al procesar tu consulta. Por favor, inténtalo de nuevo más tarde.',
            type: 'bot'
          });
          this.isLoading = false; // Restablecer isLoading a false después de error
        });
    },
    scrollToBottom () {
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
        }
      });
    },
    formatMessage (text) {
      if (!text) return '';
      // Escapar HTML para evitar inyecciones de código
      const escapedText = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

      // Convertir saltos de línea a etiquetas <br>
      return escapedText.replace(/\n/g, '<br>');
    }
  },
  watch: {
    messages () {
      this.scrollToBottom();
    },
    isOpen (val) {
      if (val) {
        this.scrollToBottom();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../stylesheets/variables.scss";

.chatbot-container {
  position: fixed;
  bottom: $sp-margin-larger;
  right: $sp-margin-larger;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.chatbot-button {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: $black;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s, background-color 0.3s;

  &:hover {
    background-color: darken($black, 10%);
    transform: scale(1.05);
  }

  &.active {
    background-color: $primary;
  }
}

.chatbot-icon {
  color: $white;
  font-size: $fs-xl;
}

.chatbot-window {
  position: absolute;
  bottom: 70px;
  right: 0;
  width: 500px;
  height: 600px;
  background-color: $white;
  border-radius: $sp-margin;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform-origin: bottom right;
}

.chatbot-header {
  background-color: $primary;
  color: $white;
  padding: $sp-margin;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chatbot-title {
  font-weight: bold;
  font-size: $fs-l;
}

.close-button {
  cursor: pointer;
  padding: $sp-margin-smaller;

  &:hover {
    opacity: 0.8;
  }
}

.chatbot-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: $sp-margin;
  overflow: hidden;
}

.chatbot-messages {
  flex: 1;
  overflow-y: auto;
  padding-right: $sp-margin-smaller;
  margin-bottom: $sp-margin-smaller;
  max-height: calc(100% - 50px);
}

.message {
  margin-bottom: $sp-margin;
  max-width: 80%;
  display: flex;

  .message-content {
    padding: 1.2rem;
    border-radius: $sp-margin;
    word-break: break-word;
    white-space: normal;
    line-height: 1.5;
    font-size: 1.2rem;
  }
}

.user-message {
  justify-content: flex-end;
  margin-left: auto;
  width: 100%;

  .message-content {
    background-color: $primary;
    color: $white;
    border-top-right-radius: 0;
    text-align: right;
  }
}

.bot-message {
  justify-content: flex-start;
  margin-right: auto;
  width: 100%;

  .message-content {
    background-color: $black-light9;
    color: $black;
    border-top-left-radius: 0;
    text-align: left;
    padding: 1.2rem;
  }
}

.chatbot-input {
  display: flex;
  border-top: 1px solid $black-trans2;
  padding-top: $sp-margin;
  height: 50px;
  align-items: center;
  font-size: 1.2rem;

  input {
    flex: 1;
    border: 1px solid $black-trans2;
    border-radius: $sp-margin;
    padding: $sp-margin-smaller $sp-margin;
    font-size: 1.2rem;
    outline: none;
    height: 38px;

    &:focus {
      border-color: $primary;
    }
  }

  .send-button {
    background-color: $primary;
    color: $white;
    border: none;
    border-radius: 50%;
    width: 38px;
    height: 38px;
    margin-left: $sp-margin-smaller;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: background-color 0.3s;

    &:hover {
      background-color: darken($primary, 10%);
    }
  }
}

.message-buttons {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-top: -5px;
  padding: 0 10px 0 50px;
  width: 100%;
  min-width: 260px;

  .btn-response {
    padding: 8px 20px;
    border: none;
    border-radius: 20px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 14px;
    width: 120px;
    margin-right: 10px;
    margin-bottom: 8px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .btn-yes {
    background-color: $primary;
    color: $white;

    &:hover {
      background-color: darken($primary, 10%);
    }
  }

  .btn-no {
    background-color: $black;
    color: $white;

    &:hover {
      background-color: darken($black, 10%);
    }
  }
}

.feedback-buttons-container {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-bottom: $sp-margin;
}

// Animaciones
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s;
}

.slide-enter,
.slide-leave-to {
  transform: scale(0.5);
  opacity: 0;
}

.typing-indicator {
  display: inline-block;
  width: 60px;
  height: 16px;
  border-radius: 8px;
  background-color: $primary;
  position: relative;
  overflow: hidden;
  padding: 0 10px;

  span {
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: $error;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    animation: typing 0.7s infinite;
  }

  span:nth-child(1) {
    left: 10px;
    animation-delay: 0s;
  }

  span:nth-child(2) {
    left: 26px;
    animation-delay: 0.2s;
  }

  span:nth-child(3) {
    left: 42px;
    animation-delay: 0.4s;
  }
}

@keyframes typing {
  0%, 100% {
    transform: translateY(-50%) scale(1);
  }
  50% {
    transform: translateY(-50%) scale(1.2);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/shared/chatbot/Chatbot.vue